/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2023, Zumper
 * @description PinMarkerListingPreview
 */

import { ChevronLeft, ChevronRight, Close } from '@zcode-ui/icons'
import type { Listable, Pin, RawPinInventorySearch } from '@zcode/state-type'
import React, { type MouseEvent, useState } from 'react'

import type { MixpanelMaplistClickType } from '../../../../types'
import { PinMarkerListingPreview } from '../PinMarkerListingPreview'

export interface PinMarkerGroupPreviewProps {
  isMobile?: boolean
  mixpanelMaplistClick: ({
    buttonClicked,
    entity,
    pageName,
    section,
  }: MixpanelMaplistClickType) => void
  onClose: () => void
  previewListable: ReadonlyArray<{
    pin: Listable | Pin<RawPinInventorySearch>
    showPriceMarker: boolean
  }>
}

export const PinMarkerGroupPreview = ({
  isMobile,
  mixpanelMaplistClick,
  onClose,
  previewListable,
}: PinMarkerGroupPreviewProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)

  const previousSlide = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setCurrentIndex(
      (prevIndex) => (prevIndex - 1 + previewListable.length) % previewListable.length
    )
  }

  const nextSlide = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setCurrentIndex((prevIndex) => (prevIndex + 1) % previewListable.length)
  }

  return (
    <div
      key={previewListable[0].pin.uId}
      onDoubleClick={(e: MouseEvent<HTMLDivElement>) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <div className="relative flex h-[44px] items-center justify-center text-gray-800 text-sm">
        {currentIndex + 1} of {previewListable.length} units
        <button
          className="absolute left-[8px] top-[12px] h-5 min-w-[20px]"
          onClick={onClose}
          type="button"
        >
          <Close fill="#726F6C" size={12} strokeWidth={2} />
        </button>
        <div className={`absolute right-[15px] top-[11px] flex ${isMobile ? 'gap-4' : 'gap-2'}`}>
          <button className="h-5 min-w-5" onClick={previousSlide} type="button">
            <ChevronLeft fill="#726F6C" />
          </button>
          <button className="h-5 min-w-5" onClick={nextSlide} type="button">
            <ChevronRight fill="#726F6C" />
          </button>
        </div>
      </div>
      <div
        className={`flex transition-transform duration-500 ease-in-out ${
          isMobile ? 'gap-3 pl-2' : 'gap-4 pl-4'
        }`}
        style={{
          transform: isMobile
            ? `translateX(calc(-${currentIndex} * (100vw - 46px)))`
            : `translateX(-${currentIndex * 270}px)`,
        }}
      >
        {previewListable.map((listable, i) => {
          return (
            <div
              key={`preview_listable_${i}`}
              style={{
                minWidth: isMobile ? 'calc(100vw - 58px)' : '254px',
              }}
            >
              <PinMarkerListingPreview
                isGroup
                mixpanelMaplistClick={mixpanelMaplistClick}
                key={listable.pin.uId}
                listable={listable.pin}
                onClose={onClose}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
